import {useCallback, useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import {graphql} from 'react-relay';
import Util from '../../../../shared/util/util';
import DeleteTaskSetMutation from '../../../../../mutations/delete_task_set_mutation';
import {useForecastFetchQuery} from '../../../../shared/hooks/useForecastFetchQuery';
import {createToast} from '../../../../shared/components/toasts/toast';
import {trackTaskSetEvent} from './taskSetTelemetry';
import {showDeleteTaskSetModal, BlockedTaskSetInstance} from '../../../../../containers/modal/task-set/DeleteTaskSetModal';

/**
 * The backend's refusal code, as it appears in the error a rejected mutation
 * carries. The only failure this hook interprets rather than passing on.
 */
const DELETE_BLOCKED_CODE = 'TASK_SET_DELETE_BLOCKED';

/**
 * Everything the confirmation needs, in one round trip, keyed to the set it was
 * asked about.
 *
 * The preflight is read here rather than selected on either surface's fragment.
 * Selecting it on a list would cost one call per set just to draw the list, and
 * the answer is per viewer rather than per set -- two of the blocking reasons
 * turn on the viewer's own permissions -- so it must not be cached beside the
 * set either.
 */
const deletePreflightQuery = graphql`
	query useDeleteTaskSetQuery($id: ID!) {
		node(id: $id) {
			... on TaskSet {
				id
				name
				instanceIds
				deletionPreflight {
					deletable
					instanceCount
					blockedInstances {
						companyTaskId
						sequence
						reason
					}
				}
			}
		}
	}
`;

interface PreflightResult {
	node?: {
		id?: string | null;
		name?: string | null;
		instanceIds?: readonly string[] | null;
		deletionPreflight?: {
			deletable?: boolean | null;
			instanceCount?: number | null;
			blockedInstances?: ReadonlyArray<BlockedTaskSetInstance> | null;
		} | null;
	} | null;
}

/**
 * The delete-a-set flow, shared by the drawer and the manage panel so the two
 * entry points cannot drift into two different confirmations.
 *
 * `startDelete` never decides anything itself. It asks the server what blocks
 * the set and then shows the matching state of the one modal -- which is also
 * why neither caller disables its control on a guess: a Delete greyed out with
 * no explanation strands a reader who cannot see that an invoiced instance is
 * the reason, or that unlocking the period would clear it.
 */
export const useDeleteTaskSet = (
	projectId: string | undefined,
	onDeleted?: () => void,
	onDeleteStarted?: (taskSetId: string) => void,
	/**
	 * Opens the set's edit form on its Ends field. Passed through to the refusal,
	 * which points there in prose and can now offer the button as well. Omitted by
	 * callers with nowhere to send the reader -- the list pane, where there is no
	 * form on screen to open.
	 */
	onEndEarly?: (taskSetId: string) => void
): {startDelete: (taskSetId: string) => void; checking: boolean} => {
	const {formatMessage} = useIntl();
	const {fetch, data, loading} = useForecastFetchQuery<PreflightResult, {id: string}>(deletePreflightQuery, true);

	// The set the check was started for. A preflight that lands after the reader
	// has moved on must not open a modal about a set they are no longer looking
	// at.
	const [pendingId, setPendingId] = useState<string | null>(null);

	const startDelete = useCallback(
		(taskSetId: string) => {
			if (loading) return;
			/* fetch FIRST, which clears the last preflight, and only then name the
			   set being checked. React 16 does not batch a setState made from a
			   callback like this one, so naming it first rendered with the previous
			   answer still in hand -- and for a re-check of the SAME set the effect
			   below could not tell that answer from a fresh one. */
			fetch({id: taskSetId}, undefined, () => {
				// Silence here would leave the reader pressing Delete and watching
				// nothing happen, with no way to tell a refusal from a dead button.
				setPendingId(null);
				createToast({duration: 4000, message: formatMessage({id: 'task_set.preflight_failed'})});
			});
			setPendingId(taskSetId);
		},
		[fetch, loading, formatMessage]
	);

	const runDelete = useCallback(
		(taskSetId: string, instanceIds: readonly string[]) => {
			if (!projectId) return;
			Util.CommitMutation(
				DeleteTaskSetMutation,
				{id: taskSetId, globalProjectId: projectId, globalTaskIds: instanceIds.slice()},
				() => {
					// After the delete, so a refused one is never counted. A delete has
					// no scope -- the whole set goes or nothing does -- so the count of
					// instances is the only thing to say about it.
					trackTaskSetEvent('Deleted', () => ({_InstanceCount: instanceIds.length}));
					// Say what went, and how much. The set and its instances vanish
					// from the table at the same moment, so without this the only
					// evidence the delete worked is the absence of rows -- which reads
					// the same as a filter having changed.
					createToast({
						duration: 4000,
						message: formatMessage({id: 'task_set.delete.deleted'}, {count: instanceIds.length}),
					});
					onDeleteStarted?.(taskSetId);
					onDeleted?.();
				},
				true,
				null,
				/* ONE error is handled here: the set became blocked between the
				   preflight the reader saw and the Delete they pressed -- someone
				   invoiced an instance in the meantime. Asking again puts the real
				   refusal back on screen, naming what changed.

				   Everything else goes straight to the fallback. Re-running the
				   preflight for any failure would swallow network errors,
				   permission errors and 500s: the preflight usually succeeds, so
				   the reader would be shown the confirmation again and never told
				   anything had gone wrong. */
				(transaction: unknown, onFailure?: (t: unknown) => boolean | void) => {
					const blocked = String(transaction).includes(DELETE_BLOCKED_CODE);
					if (!blocked) {
						if (onFailure) onFailure(transaction);
						return;
					}
					// Same order as startDelete, and for the same reason: this is a
					// re-check of the set just refused, so the stale answer and the
					// fresh one carry the same id.
					fetch({id: taskSetId}, undefined, () => {
						setPendingId(null);
						if (onFailure) onFailure(transaction);
					});
					setPendingId(taskSetId);
				}
			);
		},
		[projectId, onDeleted, onDeleteStarted, fetch, formatMessage]
	);

	const node = data?.node;
	const preflight = node?.deletionPreflight;

	useEffect(() => {
		if (!pendingId || !node || !preflight) return;
		if (node.id !== pendingId) return;
		setPendingId(null);
		const instanceIds = node.instanceIds || [];
		showDeleteTaskSetModal({
			setName: node.name || '',
			instanceCount: preflight.instanceCount ?? 0,
			// An empty list is the proceeding state. The modal reads the list rather
			// than `deletable` so the two can never disagree about which state to
			// draw while claiming different reasons.
			blockedInstances: preflight.deletable ? [] : preflight.blockedInstances || [],
			onDelete: () => runDelete(pendingId, instanceIds),
			onEndEarly: onEndEarly ? () => onEndEarly(pendingId) : undefined,
		});
	}, [pendingId, node, preflight, runDelete, onEndEarly]);

	return {startDelete, checking: loading};
};
